import { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  XCircle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
} from 'lucide-react'
import type {
  AppWithStats,
  ErrorRateTrend,
  ResponseTimeTrend,
  UptimeTrend,
  ErrorGroup,
} from '../types'

// =============================================================================
// Types
// =============================================================================

export type TimePeriod = '24h' | '7d' | '30d'

export interface SimpleDashboardChartProps {
  apps: AppWithStats[]
  errorTrends: ErrorRateTrend[]
  responseTrends: ResponseTimeTrend[]
  uptimeTrends: UptimeTrend[]
  selectedAppId: string | null
  onAppChange: (appId: string | null) => void
  timePeriod: TimePeriod
  onTimePeriodChange: (period: TimePeriod) => void
  loading?: boolean
  onViewAllErrors?: () => void
  // New: actual error groups for clickable list
  recentErrorGroups?: ErrorGroup[]
  onErrorClick?: (appId: string, errorGroupId: string) => void
}

interface RecentError {
  timestamp: string
  count: number
  message?: string
  appName?: string
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatTimestamp(timestamp: string, period: TimePeriod): string {
  const date = new Date(timestamp)
  if (period === '24h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatRelativeTime(timestamp: string): string {
  const now = new Date()
  const date = new Date(timestamp)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function truncateMessage(message: string, maxLength: number = 60): string {
  if (message.length <= maxLength) return message
  return message.substring(0, maxLength) + '...'
}

function calculateTrend(data: number[]): 'up' | 'down' | 'flat' {
  if (data.length < 2) return 'flat'
  const recent = data.slice(-Math.ceil(data.length / 3))
  const earlier = data.slice(0, Math.ceil(data.length / 3))
  const recentAvg = recent.reduce((a, b) => a + b, 0) / recent.length
  const earlierAvg = earlier.reduce((a, b) => a + b, 0) / earlier.length
  const change = ((recentAvg - earlierAvg) / (earlierAvg || 1)) * 100
  if (Math.abs(change) < 5) return 'flat'
  return change > 0 ? 'up' : 'down'
}

function TrendIndicator({
  trend,
  inverse = false,
}: {
  trend: 'up' | 'down' | 'flat'
  inverse?: boolean
}) {
  const isGood = inverse ? trend === 'up' : trend === 'down'
  const isBad = inverse ? trend === 'down' : trend === 'up'

  if (trend === 'flat') {
    return <Minus className="w-4 h-4 text-slate-400" />
  }
  if (trend === 'up') {
    return <TrendingUp className={`w-4 h-4 ${isGood ? 'text-green-500' : 'text-red-500'}`} />
  }
  return <TrendingDown className={`w-4 h-4 ${isBad ? 'text-red-500' : 'text-green-500'}`} />
}

// =============================================================================
// Component
// =============================================================================

export function SimpleDashboardChart({
  apps,
  errorTrends,
  responseTrends,
  uptimeTrends,
  selectedAppId,
  onAppChange,
  timePeriod,
  onTimePeriodChange,
  loading = false,
  onViewAllErrors,
  recentErrorGroups,
  onErrorClick,
}: SimpleDashboardChartProps) {
  // Calculate stats from trends
  const stats = useMemo(() => {
    const totalErrors = errorTrends.reduce((sum, t) => sum + (t.error_count || 0), 0)
    const errorValues = errorTrends.map(t => t.error_count || 0)
    const errorTrend = calculateTrend(errorValues)

    const avgLatency =
      responseTrends.length > 0
        ? Math.round(
            responseTrends.reduce((sum, t) => sum + (t.avg_response_time || 0), 0) /
              responseTrends.length
          )
        : 0
    const latencyValues = responseTrends.map(t => t.avg_response_time || 0)
    const latencyTrend = calculateTrend(latencyValues)

    const avgUptime =
      uptimeTrends.length > 0
        ? uptimeTrends.reduce((sum, t) => sum + (t.uptime_percentage || 0), 0) / uptimeTrends.length
        : 100
    const uptimeValues = uptimeTrends.map(t => t.uptime_percentage || 0)
    const uptimeTrend = calculateTrend(uptimeValues)

    return { totalErrors, errorTrend, avgLatency, latencyTrend, avgUptime, uptimeTrend }
  }, [errorTrends, responseTrends, uptimeTrends])

  // Prepare chart data
  const chartData = useMemo(() => {
    return errorTrends.map((e, i) => ({
      timestamp: e.timestamp,
      errors: e.error_count || 0,
      latency: responseTrends[i]?.avg_response_time || 0,
      uptime: uptimeTrends[i]?.uptime_percentage || 100,
    }))
  }, [errorTrends, responseTrends, uptimeTrends])

  // Get recent errors (from error trends with counts > 0)
  const recentErrors: RecentError[] = useMemo(() => {
    return errorTrends
      .filter(t => (t.error_count || 0) > 0 && t.timestamp)
      .slice(-5)
      .reverse()
      .map(t => ({
        timestamp: t.timestamp!,
        count: t.error_count || 0,
      }))
  }, [errorTrends])

  return (
    <div className="space-y-4">
      {/* Header: App filter + Time period */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        {/* App Filter */}
        <div className="flex items-center gap-2">
          <select
            value={selectedAppId || ''}
            onChange={e => onAppChange(e.target.value || null)}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">
              All Apps ({apps.reduce((sum, a) => sum + (a.unresolved_errors || 0), 0)} errors)
            </option>
            {apps.map(app => (
              <option key={app.id} value={app.id}>
                {app.name}
                {app.unresolved_errors > 0 ? ` (${app.unresolved_errors})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Time Period */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
          {(['24h', '7d', '30d'] as const).map(period => (
            <button
              key={period}
              onClick={() => onTimePeriodChange(period)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                timePeriod === period
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-3 gap-4">
        {/* Errors */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-slate-500">Errors</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-2xl font-bold ${stats.totalErrors > 0 ? 'text-red-600' : 'text-slate-900'}`}
            >
              {stats.totalErrors.toLocaleString()}
            </span>
            <TrendIndicator trend={stats.errorTrend} />
          </div>
        </div>

        {/* Uptime */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-sm text-slate-500">Uptime</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-slate-900">{stats.avgUptime.toFixed(1)}%</span>
            <TrendIndicator trend={stats.uptimeTrend} inverse />
          </div>
        </div>

        {/* Latency */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-blue-500" />
            <span className="text-sm text-slate-500">Avg Latency</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-slate-900">{stats.avgLatency}ms</span>
            <TrendIndicator trend={stats.latencyTrend} />
          </div>
        </div>
      </div>

      {/* Error Trend Chart */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-slate-900">Error Trend</h3>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-red-500 rounded" />
              <span>Errors</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500" />
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
            No data available
          </div>
        ) : (
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer width="100%" height={160}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="timestamp"
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickFormatter={value => formatTimestamp(value, timePeriod)}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: 12,
                  }}
                  labelFormatter={value => {
                    const date = new Date(value)
                    return date.toLocaleString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  }}
                  formatter={value => [value ?? 0, 'Errors']}
                />
                <Line
                  type="monotone"
                  dataKey="errors"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: '#ef4444' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Recent Errors List */}
      {/* Show error groups if an app is selected and we have them, otherwise show aggregated trends */}
      {recentErrorGroups && recentErrorGroups.length > 0 && selectedAppId ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-slate-900">Recent Errors</h3>
            {onViewAllErrors && (
              <button
                onClick={onViewAllErrors}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                View all
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="space-y-2">
            {recentErrorGroups.slice(0, 5).map(errorGroup => (
              <button
                key={errorGroup.id}
                onClick={() => onErrorClick?.(errorGroup.app_id, errorGroup.id)}
                className="w-full flex items-center justify-between text-sm py-2 px-2 -mx-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span
                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                      errorGroup.level === 'error'
                        ? 'bg-red-500'
                        : errorGroup.level === 'warning'
                          ? 'bg-amber-500'
                          : 'bg-slate-400'
                    }`}
                  />
                  <span className="text-slate-700 truncate">
                    {truncateMessage(errorGroup.message)}
                  </span>
                  {errorGroup.occurrence_count > 1 && (
                    <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded flex-shrink-0">
                      ×{errorGroup.occurrence_count}
                    </span>
                  )}
                </div>
                <span className="text-slate-400 text-xs flex-shrink-0 ml-2">
                  {formatRelativeTime(errorGroup.last_seen_at)}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        recentErrors.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-900">Error Activity</h3>
            </div>
            <div className="space-y-2">
              {recentErrors.map((error, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-slate-600">
                      {error.count} error{error.count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="text-slate-400 text-xs">
                    {formatTimestamp(error.timestamp, timePeriod)}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-3">Select an app to see individual errors</p>
          </div>
        )
      )}
    </div>
  )
}

export default SimpleDashboardChart
